import { NoticeBanner, type NoticePriority } from "@apac/ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import styles from "./ConnectedNoticeBanner.module.scss";

export interface NoticeLanguageContent {
  title: string;
  description: string;
}

// `en` is required and is the ultimate fallback. Other languages (e.g. `ar`,
// `zh`, `hi`, or fully-qualified `zh-HK`) can be added as sibling keys.
export interface Notice {
  en: NoticeLanguageContent;
  priority: NoticePriority;
  enabled: boolean;
  startAt?: string;
  endAt?: string;
  [language: string]: NoticeLanguageContent | unknown;
}

export interface NoticesContainer {
  notices: Notice[];
}

export interface ConnectedNoticeBannerProps {
  notices: NoticesContainer | undefined;
  language: string;
  ctaText: string;
}

const isLanguageContent = (value: unknown): value is NoticeLanguageContent =>
  typeof value === "object" &&
  value != null &&
  typeof (value as NoticeLanguageContent).title === "string" &&
  typeof (value as NoticeLanguageContent).description === "string";

const pickLanguageContent = (
  notice: Notice,
  language: string
): NoticeLanguageContent => {
  const exact = notice[language];
  if (isLanguageContent(exact)) return exact;
  const base = language.split("-")[0];
  if (base !== language) {
    const baseContent = notice[base];
    if (isLanguageContent(baseContent)) return baseContent;
  }
  return notice.en;
};

// Parses an ISO date string from the LD payload into epoch ms.
// `undefined` → `fallback` (use ±Infinity to mean "unbounded").
// Malformed string → `null`, signalling the caller to drop the notice.
const parseTime = (
  value: string | undefined,
  fallback: number
): number | null => {
  if (value == null) return fallback;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

export const ConnectedNoticeBanner = ({
  notices,
  language,
  ctaText,
}: ConnectedNoticeBannerProps) => {
  const [, cn] = useModuleBEM(styles, "ConnectedNoticeBanner");

  if (notices == null) return null;

  const activeNotices = notices.notices.filter((notice) => notice.enabled);

  return (
    <>
      {activeNotices.map((notice) => {
        const strings = pickLanguageContent(notice, language);
        const now = Date.now();
        const startAt = parseTime(notice.startAt, 0);
        const endAt = parseTime(notice.endAt, Infinity);
        if (startAt == null || endAt == null) return null;
        if (now < startAt || now > endAt) return null;
        return (
          <NoticeBanner
            bannerClassName={cn("banner")}
            key={strings.title}
            title={strings.title}
            description={strings.description}
            priority={notice.priority}
            ctaText={ctaText}
          />
        );
      })}
    </>
  );
};
